import { useEffect, useRef, useState } from 'react';
import { Badge, Button } from '@indipia/ui-core';
import { ThumbImg } from '../ThumbImg';
import type { FieldReportListItem } from '../../types/worklog.types';
import { fmtReportDtm, fmtWorkDate, type Designation, type WorkMode } from './feedModel';

// ── 현장보고(FR) 카드 ────────────────────────────────────────
// 상세 페이지는 폐기(§5.6) — 이 화면은 카드만으로 표시를 끝낸다(인라인 펼침·이동 없음).
// 고객공유 선정 UI는 제거됨(REQ-4-06, 결정 4-8 — 진입점5로 이관). 인디포토 저장(REQ-4-04)만 남는다.
export function FieldReportCard({
  item,
  workMode,
  readOnly = false,
  saving,
  designation,
  onTogglePhoto,
  onEnterWork,
  onCancelWork,
  onSaveWork,
  onOpenPhoto,
}: {
  item: FieldReportListItem;
  /** 이 카드가 선정 대상일 때의 모드(인디포토만 남음). 대상이 아니면 null(평소 카드). */
  workMode: WorkMode;
  /** 열람 전용(거래완료 현장) — 사진첩에 담기([사진선택])를 숨긴다. 이미 담은 사진 표시(포토 배지·✓)는 그대로 보인다. */
  readOnly?: boolean;
  /** 이 카드 저장 진행 중 — 완료 버튼 중복 클릭 방지 */
  saving: boolean;
  designation: Designation;
  onTogglePhoto: (idx: number) => void;
  onEnterWork: () => void;
  onCancelWork: () => void;
  onSaveWork: () => void;
  onOpenPhoto: (photoIdx: number) => void;
}) {
  // 작업상태 배지 판정 — 현장보고 목록(진입점3)과 동일 우선순위(이슈발생 > 작업완료 > 진행중).
  const workTypeNames = item.workTypeNames?.length ? item.workTypeNames : item.workTypeCodes;
  const isIssue = !!item.issueTypeCodes?.length;
  const isComplete = !isIssue && item.workCmplYn === 'Y';
  const issueNames = item.issueTypeNames?.length ? item.issueTypeNames : (item.issueTypeCodes ?? []);

  /*
   * 본문 3줄 초과 여부 — 초과분은 **다음 줄에 '…' 한 줄**로 알린다.
   * `-webkit-line-clamp`는 3번째 줄 끝에 '…'를 붙여, 그 줄의 문장이 잘린 것처럼 보였다.
   * 그래서 clamp 대신 max-height로 3줄만 노출하고(잘림 표시 없음) 넘칠 때만 '…' 줄을 덧댄다.
   * 2열↔1열 전환·창 크기 변경으로 줄 수가 바뀌므로 ResizeObserver로 다시 잰다.
   */
  const bodyRef = useRef<HTMLParagraphElement | null>(null);
  const [clipped, setClipped] = useState(false);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) {
      setClipped(false); // 선정 모드로 전환돼 본문이 언마운트된 경우
      return;
    }
    const measure = () => setClipped(el.scrollHeight - el.clientHeight > 1); // 반올림 오차 1px 허용
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [item.reportContent, workMode]);

  const ipCount = designation.ipPhotos.size;
  const hasIP = ipCount > 0;

  return (
    <article
      className={[
        // 2열 보기에서 같은 행의 카드끼리 높이를 맞추고(h-full), 하단 [인디포토] 바를
        // 본문 길이와 무관하게 바닥에 붙인다(flex-col + 아래 mt-auto).
        'flex h-full flex-col overflow-hidden rounded-lg border bg-surface-raised p-4',
        workMode === 'photo' ? 'border-info' : 'border-border',
      ].join(' ')}
    >
      {/* 헤더 한 줄 — 작성자·공종·상태 배지(REQ-4-05). 현장명·타입배지는 실측 목업에 없어 제거(화면 상단에서 한 번만 표시).
          작성 시각은 아래 '작성일시/작업일' 블록에서 보여준다. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="shrink-0 text-body-md font-semibold text-text">{item.writerName}</span>
        {workTypeNames.map((name, i) => (
          <Badge key={`wt-${name}-${i}`} status="info">{name}</Badge>
        ))}
        {isComplete && <Badge status="success">작업완료</Badge>}
        {issueNames.map((name, i) => (
          <Badge key={`issue-${name}-${i}`} status="warning">{name}</Badge>
        ))}
        {item.reportSource === 'OWNER' && <Badge status="warning">사장님 작성</Badge>}
        {hasIP && <Badge status="info" className="ml-auto shrink-0">포토</Badge>}
      </div>

      {/* 작성일시(제출 시각) · 작업일 — 보고 내용 위(D/C-3 카드와 동일 표기). 라벨 연하게·값 진하게, 값 시작 위치 정렬 */}
      <dl className="mt-03 grid grid-cols-[auto_1fr] items-baseline gap-x-sm gap-y-sm text-caption">
        <dt className="text-text-muted">작성일시:</dt>
        <dd className="font-medium text-text">{fmtReportDtm(item.reportDtm)}</dd>
        <dt className="text-text-muted">작업일:</dt>
        <dd className="font-medium text-text">{fmtWorkDate(item.workDate)}</dd>
      </dl>

      <p
        ref={bodyRef}
        className="mt-2 text-body-md text-text-secondary"
        style={{
          // 정확히 3줄만 노출 — lineHeight를 명시해 maxHeight가 딱 3줄에 떨어지게 한다
          // (부분 노출된 4번째 줄이 얼비치지 않도록).
          lineHeight: 1.625, // = Tailwind leading-relaxed
          maxHeight: 'calc(1.625em * 3)',
          overflow: 'hidden',
          whiteSpace: 'pre-line',
          opacity: workMode === 'photo' ? 0.45 : 1,
        }}
      >
        {item.reportContent}
      </p>
      {/* 3줄 초과 — 잘린 문장 끝이 아니라 다음 줄에 별도로 알린다(문장 자체의 생략과 구분) */}
      {!workMode && clipped && (
        <div
          className="text-body-md text-text-muted"
          style={{ lineHeight: 1.625 }}
          aria-label="내용 더 있음"
        >
          …
        </div>
      )}

      {/* 사진 */}
      {item.photoCount > 0 && workMode && (
        <div className="mt-2.5 text-caption text-text-muted">사진도 클릭하여 선정/해제</div>
      )}
      {item.photoCount > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {item.photos.map((p, pi) => {
            const selIP = designation.ipPhotos.has(pi);
            const inWork = workMode === 'photo';
            const handlePhoto = inWork ? () => onTogglePhoto(pi) : () => onOpenPhoto(pi);
            return (
              <button
                key={p.frPhotoId}
                type="button"
                aria-label={inWork ? `사진 ${pi + 1} 선정` : `사진 ${pi + 1} 크게보기`}
                onClick={(e) => { e.stopPropagation(); handlePhoto(); }}
                className={[
                  'relative h-14 w-14 overflow-hidden rounded-md border-[1.5px] p-0',
                  inWork && selIP ? 'border-info' : '',
                ].join(' ')}
                style={inWork && selIP ? undefined : { borderColor: 'var(--gray-400)' }}
              >
                <ThumbImg url={p.thumbUrl} className="h-full w-full object-cover" />
                {/* 선정 모드 체크 오버레이 */}
                {inWork && (
                  <span
                    className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full text-caption font-bold text-white"
                    style={{ background: selIP ? 'var(--color-info)' : 'var(--alpha-black-400)' }}
                  >
                    {selIP ? '✓' : ''}
                  </span>
                )}
                {/* 일반 보기 지정 뱃지 — 썸네일 위에서 눈에 띄게(흰 링+그림자로 대비) */}
                {!inWork && selIP && (
                  <span className="absolute right-0.5 top-0.5">
                    <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-info text-caption font-bold leading-none text-white shadow-sm ring-1 ring-[var(--alpha-white-800)]">
                      ✓
                    </span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* 하단 인디포토 버튼(선정 모드 아닐 때) — mt-auto로 카드 바닥에 고정. 열람 전용이면 숨김 */}
      {!workMode && !readOnly && (
        <div className="mt-auto pt-3">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEnterWork(); }}
            className={[
              'flex w-full items-center gap-1.5 rounded-md border px-3 py-2 text-left transition-colors',
              hasIP ? 'border-info' : 'border-border bg-surface hover:bg-surface-muted',
            ].join(' ')}
            style={hasIP ? { background: 'var(--color-infoSubtle)' } : undefined}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={hasIP ? 'var(--color-info)' : 'var(--shell-hint)'} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" />
            </svg>
            <span className={['min-w-0 flex-1 truncate', hasIP ? 'text-body-sm font-semibold text-[color:var(--color-infoStrong)]' : 'text-body-sm font-medium text-text-secondary'].join(' ')}>
              작업보고내 첨부사진→필요한것만 나의 사진첩에 담아두기
            </span>
            {hasIP && <Badge status="info" className="shrink-0">{ipCount}</Badge>}
            <span className="shrink-0 text-caption text-text-muted">사진선택 →</span>
          </button>
        </div>
      )}

      {/* 선정 중 — 이 카드 전용 하단 바. 카드 폭 전체를 채우도록 패딩만큼 음수 마진. */}
      {workMode && (
        <div
          className="-mx-4 -mb-4 mt-auto flex items-center justify-between gap-2 px-4 py-2"
          style={{ background: 'var(--color-infoSubtle)' }}
        >
          <span className="min-w-0 truncate text-caption font-bold" style={{ color: 'var(--color-info)' }}>
            {`인디포토 선정중… ${ipCount > 0 ? `사진 ${ipCount}` : '선택 없음'}`}
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={(e) => { e.stopPropagation(); onCancelWork(); }}
            >
              취소
            </Button>
            <Button
              size="sm"
              loading={saving}
              onClick={(e) => { e.stopPropagation(); onSaveWork(); }}
            >
              완료
            </Button>
          </span>
        </div>
      )}
    </article>
  );
}
